/**
 * 軽タスク「テスト可否」セル。
 *
 * 軽タスクリストの「アサイン済み軽タスク」テーブル最右列に出す。編集権限
 * （PMOチーム / PI Div の team_leader / 管理者）があればプルダウンで選択即保存し、
 * 無ければ表示のみ行う。閉じている時も開いた候補一覧も、値は同じ**実色で塗ったラベル**
 * （OK=緑 / 準備中=青 / 不可=グレー）で見せる。トリガーの枠線・背景・キャレットは
 * `triggerVariant="bare"` で外し、ラベルそのものがクリック領域になる。
 *
 * この列は cortex 側の Firestore にのみ保存し、Backlog 等の他システムとは接続・参照しない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスクのテスト可否を色付きラベルで表示し、編集権限がある場合はラベル風の候補プルダウンで選択即保存する行セル
 * @graph-connects useUpdateLightTaskTestability [calls] テスト可否更新 ミューテーション
 */

import type { ReactNode } from 'react';
import { Picker, type PickerOption } from '@cortex/web-design-system/components/ui/Picker';
import {
  LIGHT_TASK_TESTABILITY_VALUES,
  isLightTaskTestability,
  type LightTaskTestability,
} from '@cortex/domain-product-innovation-lab-types';
import { useUpdateLightTaskTestability } from '../../../hooks/index.js';
import type { AssignedLightTaskItem } from '../../../types/project.js';

/**
 * テスト可否の値ごとの配色（OK=緑 / 準備中=青 / 不可=グレー）。枠線は持たず、実色の塗りだけで
 * ステータスを示す。色は `styles.css` の `--color-testability-*`（コントラスト比を検証済み）。
 * @graph-connects none
 */
const TESTABILITY_COLOR_CLASS: Record<LightTaskTestability, string> = {
  OK: 'bg-[var(--color-testability-ok-bg)] text-[var(--color-testability-ok-text)]',
  準備中:
    'bg-[var(--color-testability-preparing-bg)] text-[var(--color-testability-preparing-text)]',
  不可: 'bg-[var(--color-testability-blocked-bg)] text-[var(--color-testability-blocked-text)]',
};

/**
 * 未設定ラベルの配色。「値が無い」状態でステータスではないため塗らず、面色 + muted 文字で
 * 主張させない。
 * @graph-connects none
 */
const TESTABILITY_UNSET_CLASS = 'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]';

/** 候補プルダウンの value。空文字は「未設定」（null）に対応する。 @graph-connects none */
const TESTABILITY_OPTIONS: PickerOption[] = [
  { value: '', label: '未設定' },
  ...LIGHT_TASK_TESTABILITY_VALUES.map((value) => ({ value, label: value })),
];

/**
 * テスト可否ラベル（チップ）。トリガー・候補一覧・閲覧専用表示で同じ見た目を使う。
 * @graph-connects none
 */
function TestabilityLabel({ value }: { value: LightTaskTestability | null }): ReactNode {
  return (
    <span
      className={`inline-flex w-fit shrink-0 items-center self-start rounded-full px-2.5 py-1 text-xs font-medium leading-none ${
        value === null ? TESTABILITY_UNSET_CLASS : TESTABILITY_COLOR_CLASS[value]
      }`}
    >
      {value ?? '未設定'}
    </span>
  );
}

/** Picker の option を `LightTaskTestability | null` に戻す。 @graph-connects none */
function toTestability(value: string | null): LightTaskTestability | null {
  return isLightTaskTestability(value) ? value : null;
}

/**
 * テスト可否セル。編集権限があればラベル風の候補プルダウン（選択即保存）、
 * 無ければ色付きラベルの表示のみを出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスク1件のテスト可否セル。編集可なら色付きラベルの候補から選んで即保存し、不可なら色付きラベル表示にする
 * @graph-connects useUpdateLightTaskTestability [calls] テスト可否更新 ミューテーション
 */
export function LightTaskTestabilityCell({
  item,
  canEdit,
}: {
  item: AssignedLightTaskItem;
  canEdit: boolean;
}): ReactNode {
  const update = useUpdateLightTaskTestability();

  if (!canEdit) {
    return item.testability === null ? (
      <span className="text-xs text-[var(--color-text-muted)]">-</span>
    ) : (
      <TestabilityLabel value={item.testability} />
    );
  }

  return (
    <div className="w-fit">
      <Picker
        aria-label={`${item.issueKey} のテスト可否`}
        options={TESTABILITY_OPTIONS}
        value={item.testability ?? ''}
        disabled={update.isPending}
        triggerVariant="bare"
        renderValue={(option) => <TestabilityLabel value={toTestability(option.value)} />}
        renderOption={(option) => <TestabilityLabel value={toTestability(option.value)} />}
        onChange={(value) => {
          update.mutate({ issueKey: item.issueKey, testability: toTestability(value) });
        }}
      />
      {update.isError && (
        <p className="mt-1 text-xs text-[var(--color-error)]">保存に失敗しました</p>
      )}
    </div>
  );
}
